/**
 * Client-side validation for the brand / brand-product / page forms.
 *
 * The API validates everything again — this only exists so the admin sees the
 * problem next to the field instead of as a snackbar after a round trip.
 * Keep the rules here in step with asa_core/src/utils/validators.js.
 */

export const IMAGE_MAX_BYTES = 5 * 1024 * 1024; // matches MAX_IMAGE_SIZE

export const ALLOWED_IMAGE_MIME = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
  "image/svg+xml",
];

// Section types the API accepts, with the labels shown in the panel.
export const SECTION_TYPES = [
  { value: "hero", label: "Hero — heading + sub-heading (+ image)" },
  { value: "rich_text", label: "Rich text — heading + formatted body" },
  { value: "icon_list", label: "Icon list — icon / title / description rows" },
  { value: "image_text", label: "Image + text — rows beside a picture" },
  { value: "logo_grid", label: "Logo grid — logo cards (certifications etc.)" },
  { value: "cta", label: "Call to action — heading, text and a button" },
  { value: "gallery", label: "Gallery — a grid of images" },
];

export const BRAND_GROUPS = [
  { value: "brand", label: 'Brand — lists under "Our Brands"' },
  { value: "range", label: 'Range — lists under "Our Range"' },
];

// Icon names the website maps to a component. Free text is allowed too; this is
// only the picker's suggestion list.
export const ICON_SUGGESTIONS = [
  "Leaf",
  "Recycle",
  "Lightbulb",
  "Globe",
  "BadgeCheck",
  "Users",
  "Handshake",
  "MapPin",
  "Mail",
  "Phone",
  "Instagram",
  "PackageOpen",
  "PawPrint",
  "Check",
];

export const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/** Pulls the API's message out of an axios error, with a sensible fallback. */
export const apiErrorMessage = (error, fallback = "Something went wrong") => {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;

  if (data.message && Array.isArray(data.fields) && data.fields.length) {
    return `${data.message} (${data.fields.join(", ")})`;
  }
  return data.message || fallback;
};

/** First error message in an errors object, for the snackbar. */
export const firstError = (errors) => {
  const key = Object.keys(errors || {})[0];
  return key ? errors[key] : "";
};

export const hasErrors = (errors) => Object.keys(errors || {}).length > 0;

/** Validates a picked file before it is sent. Returns "" when it is fine. */
export const validateImageFile = (file) => {
  if (!file) return "";

  if (!ALLOWED_IMAGE_MIME.includes(file.type)) {
    return "Only image files are allowed (JPG, PNG, WEBP, GIF, AVIF, SVG)";
  }
  if (file.size > IMAGE_MAX_BYTES) {
    return `Image must be ${IMAGE_MAX_BYTES / (1024 * 1024)} MB or smaller`;
  }
  return "";
};

const isBlank = (value) => !String(value ?? "").trim();

/* ------------------------------------------------------------------ brands */

export const validateBrand = (values, { isUpdate = false, hasLogo = false } = {}) => {
  const errors = {};

  if (isBlank(values.name)) errors.name = "Brand name is required";
  else if (values.name.trim().length > 300) errors.name = "Brand name is too long";

  // On create the slug is derived from the name when left blank, so it is only
  // required if the admin cleared a derived value.
  if (!isUpdate && isBlank(values.slug) && isBlank(values.name)) {
    errors.slug = "URL key is required";
  }
  if (!isBlank(values.slug) && !/^[a-z0-9-]+$/.test(values.slug.trim())) {
    errors.slug = "URL key may only hold lowercase letters, numbers and hyphens";
  }

  if (!["brand", "range"].includes(values.group)) {
    errors.group = "Choose whether this is a brand or a range";
  }

  if (!isUpdate && !hasLogo && !values.logo_file && values.group === "brand" && isBlank(values.badge)) {
    // Not fatal — the site falls back to the badge text — but worth flagging.
    errors.logo = "Upload a logo, or set badge text to show in its place";
  }

  const fileError = validateImageFile(values.logo_file);
  if (fileError) errors.logo = fileError;

  if (values.sort_order !== "" && Number.isNaN(Number(values.sort_order))) {
    errors.sort_order = "Display order must be a number";
  } else if (Number(values.sort_order) < 0) {
    errors.sort_order = "Display order cannot be negative";
  }

  if (!isBlank(values.page_route) && !values.page_route.trim().startsWith("/")) {
    errors.page_route = 'Page route should start with "/" (e.g. /value-nest)';
  }

  ["primary_color", "secondary_color", "background_color", "border_color"].forEach(
    (key) => {
      const value = values[key];
      if (!isBlank(value) && !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim())) {
        errors[key] = "Use a hex colour such as #2B6CB0";
      }
    }
  );

  return errors;
};

/* ---------------------------------------------------------- brand products */

export const validateBrandProduct = (
  values,
  { isUpdate = false, hasImage = false } = {}
) => {
  const errors = {};

  if (isBlank(values.brand)) errors.brand = "Choose the brand this product belongs to";
  if (isBlank(values.name)) errors.name = "Product name is required";
  else if (values.name.trim().length > 300) errors.name = "Product name is too long";

  if (!isUpdate && !values.image_file) errors.image = "Product image is required";

  const fileError = validateImageFile(values.image_file);
  if (fileError) errors.image = fileError;

  if (isUpdate && !hasImage && !values.image_file) {
    errors.image = "Product image is required";
  }

  if (values.sort_order !== "" && Number.isNaN(Number(values.sort_order))) {
    errors.sort_order = "Display order must be a number";
  } else if (Number(values.sort_order) < 0) {
    errors.sort_order = "Display order cannot be negative";
  }

  return errors;
};

/* ------------------------------------------------------------------- pages */

const SECTION_TYPE_VALUES = SECTION_TYPES.map((type) => type.value);

export const validateSitePage = (values, { isUpdate = false } = {}) => {
  const errors = {};

  if (isBlank(values.name)) errors.name = "Page name is required";

  if (!isUpdate && isBlank(values.slug) && isBlank(values.name)) {
    errors.slug = "URL key is required";
  }
  if (!isBlank(values.slug) && !/^[a-z0-9-]+$/.test(values.slug.trim())) {
    errors.slug = "URL key may only hold lowercase letters, numbers and hyphens";
  }

  (values.sections || []).forEach((section, index) => {
    if (!SECTION_TYPE_VALUES.includes(section.type)) {
      errors[`section_${index}_type`] = `Section ${index + 1}: choose a valid type`;
    }

    // A block with nothing in it would render as an empty gap on the site.
    const isEmpty =
      isBlank(section.heading) &&
      isBlank(section.subheading) &&
      isBlank(section.body) &&
      isBlank(section.image) &&
      !(section.items || []).length;

    if (isEmpty) {
      errors[`section_${index}_empty`] = `Section ${index + 1} is empty — add a heading, text, image or rows`;
    }

    (section.items || []).forEach((item, itemIndex) => {
      if (isBlank(item.title) && isBlank(item.description) && isBlank(item.image)) {
        errors[`section_${index}_item_${itemIndex}`] =
          `Section ${index + 1}, row ${itemIndex + 1} is empty — add a title, text or image`;
      }
      if (!isBlank(item.link) && !/^(https?:\/\/|\/|mailto:|tel:)/i.test(item.link.trim())) {
        errors[`section_${index}_item_${itemIndex}_link`] =
          `Section ${index + 1}, row ${itemIndex + 1}: link must start with "/", "http", "mailto:" or "tel:"`;
      }
    });
  });

  if (
    !isBlank(values.button_link) &&
    !/^(https?:\/\/|\/)/i.test(values.button_link.trim())
  ) {
    errors.button_link = 'Button link must start with "/" or "http"';
  }

  return errors;
};

/**
 * Client-only identity for a section or row.
 *
 * The builder reorders sections in place and mounts a rich-text editor inside
 * each one, so React needs a key that survives a move — an array index does
 * not. `_uid` is stripped again by `stripUids` before the payload is sent.
 */
let uidCounter = 0;
export const nextUid = () => {
  uidCounter += 1;
  return `s${Date.now().toString(36)}${uidCounter}`;
};

export const attachUids = (sections = []) =>
  sections.map((section) => ({
    ...section,
    _uid: section._uid || nextUid(),
    items: (section.items || []).map((item) => ({
      ...item,
      _uid: item._uid || nextUid(),
    })),
  }));

export const stripUids = (sections = []) =>
  sections.map(({ _uid, items, ...section }) => ({
    ...section,
    items: (items || []).map(({ _uid: itemUid, ...item }) => item),
  }));

/** A fresh, empty section for the builder. */
export const emptySection = (sortOrder = 0) => ({
  _uid: nextUid(),
  key: "",
  type: "rich_text",
  heading: "",
  subheading: "",
  body: "",
  image: "",
  image_alt: "",
  button_label: "",
  button_link: "",
  items: [],
  sort_order: sortOrder,
  status: "active",
});

/** A fresh, empty row inside a section. */
export const emptySectionItem = (sortOrder = 0) => ({
  _uid: nextUid(),
  icon: "",
  title: "",
  description: "",
  image: "",
  image_alt: "",
  link: "",
  sort_order: sortOrder,
});
